import type { User } from "~/composables/api/account/useUser";
import { useSetTokenExpiration } from "~/composables/useSetTokenExpiration";
import { useWebviewExtraHeaders } from "~/composables/useWebviewExtraHeaders";
import type { SignInResponse } from "~/store/auth";
import {
  isIframeBridgeAvailable,
  postIframeMessage,
  waitForIframeMessage,
  type IframeTokenResponse,
} from "~/utils/iframe/bridge";
import {
  localStorageGet,
  localStorageSet,
} from "~/utils/internet/local-storage";
import {
  isWebviewBridgeAvailable,
  postWebviewMessage,
  waitForWebviewMessage,
  type WebviewTokenResponse,
} from "~/utils/webview/bridge";

interface RefreshTokenData {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresIn: number;
  refreshTokenExpiresIn: number;
  expiresIn: number;
  account: User;
}

interface RefreshTokenResponse {
  data: RefreshTokenData;
}

export type AuthCookie = "token" | "refresh_token";

export default defineNuxtPlugin(() => {
  let refreshPromise: any = null;
  let refreshing = false;

  const env = useRuntimeConfig();
  const setRefreshTokenExpiration = useSetRefreshTokenExpiration();
  const setTokenExpiration = useSetTokenExpiration();
  const webviewExtraHeaders = useWebviewExtraHeaders();
  const cookieOptions = {
    path: "/",
    maxAge: 60 * 60 * 24 * 30,
    domain: env.public.hurstDomain as string | undefined,
  };

  const refreshTokenCookie = useCookie("refresh_token", cookieOptions);
  const refreshTokenExpCookie = useCookie("refresh_exp", cookieOptions);
  const tokenCookie = useCookie("token", cookieOptions);
  const nidCookie = useRawCookie("nid", cookieOptions);
  const accountCookie = useCookie("account", cookieOptions);

  const clearAuthCookies = () => {
    refreshTokenCookie.value = null;
    tokenCookie.value = null;
    nidCookie.value = null;
    accountCookie.value = null;

    setRefreshTokenExpiration(null);
    setTokenExpiration(null);
  };

  const token = computed(() => tokenCookie.value);

  const setAuthCookie = (cookie: AuthCookie, value: string | null) => {
    switch (cookie) {
      case "token":
        tokenCookie.value = value;
        break;
      case "refresh_token":
        refreshTokenCookie.value = value;
        break;
    }
  };

  const refreshViaWebview = async (): Promise<void> => {
    try {
      postWebviewMessage("token_refresh_request");

      const response = await waitForWebviewMessage<WebviewTokenResponse>(
        "token_refresh_response",
        30000,
      );

      tokenCookie.value = response.token;
      refreshTokenCookie.value = response.refresh_token;
      refreshTokenExpCookie.value = response.refresh_exp.toString();
    } catch {
      postWebviewMessage("webview_close", {
        errorCode: "TOKEN_REFRESH_TIMEOUT",
      });
    } finally {
      refreshCookie("token");
      refreshCookie("refresh_token");
      refreshCookie("refresh_exp");
    }
  };

  const refreshViaIframe = async (): Promise<void> => {
    try {
      postIframeMessage("token_refresh_request");

      const response = await waitForIframeMessage<IframeTokenResponse>(
        "token_refresh_response",
        30000,
      );

      tokenCookie.value = response.token;
      refreshTokenCookie.value = response.refresh_token;
      refreshTokenExpCookie.value = response.refresh_exp.toString();
    } catch {
      postIframeMessage("iframe_close", {
        errorCode: "TOKEN_REFRESH_TIMEOUT",
      });
    } finally {
      refreshCookie("token");
      refreshCookie("refresh_token");
      refreshCookie("refresh_exp");
    }
  };

  const refreshViaApi = async (): Promise<RefreshTokenData | null | void> => {
    try {
      const res = await $fetch<RefreshTokenResponse>("/token/refresh", {
        baseURL: env.public.authBaseURL,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accountCookie.value && {
            "x-account-id": accountCookie.value,
          }),
          ...webviewExtraHeaders.value,
        },
        body: {
          refreshToken: refreshTokenCookie.value,
        },
      });
      const data = res.data;

      if (!data) return null;

      refreshTokenCookie.value = data.refreshToken;
      tokenCookie.value = data.accessToken;
      nidCookie.value = data.account.nid;
      accountCookie.value = data.account.id;
      setRefreshTokenExpiration(data?.refreshTokenExpiresIn);
      setTokenExpiration(data?.accessTokenExpiresIn ?? data?.expiresIn);

      const authStoraged = localStorageGet<SignInResponse>("HCP-AUTH");

      if (authStoraged) {
        localStorageSet("HCP-AUTH", {
          ...authStoraged,
          token: data.accessToken,
          refresh_token: data.refreshToken,
        });
      }

      return data;
    } catch {
      clearAuthCookies();
    } finally {
      refreshCookie("token");
      refreshCookie("account");
      refreshCookie("refresh_token");
      refreshCookie("nid");
    }
  };

  const refresh = (): Promise<RefreshTokenData | null | void> => {
    if (isWebviewBridgeAvailable()) {
      return refreshViaWebview();
    }

    if (isIframeBridgeAvailable()) {
      return refreshViaIframe();
    }

    return refreshViaApi();
  };

  const refreshLock = async () => {
    if (refreshing) {
      try {
        await refreshPromise;
      } catch {}
    }
  };

  const performTokenRefresh = (): Promise<RefreshTokenData | null> => {
    if (refreshing) {
      return refreshPromise;
    }

    refreshPromise = refresh();
    refreshing = true;
    refreshPromise.finally(() => {
      refreshing = false;
    });
    return refreshPromise;
  };

  return {
    provide: {
      performTokenRefresh,
      refreshLock,
      token,
      setAuthCookie,
    },
  };
});
